import React, { useEffect, useState } from "react";
import { useParams, Link } from "react-router-dom";
import api from "@/lib/api";
import { useCart } from "@/context/CartContext";
import { Minus, Plus, Star, ShoppingBag, Truck, Leaf, ArrowLeft } from "lucide-react";
import { Button } from "@/components/ui/button";

const ProductDetail = () => {
  const { id } = useParams();
  const { addItem } = useCart();
  const [product, setProduct] = useState(null);
  const [qty, setQty] = useState(1);

  useEffect(() => {
    api.get(`/products/${id}`).then((r) => setProduct(r.data));
  }, [id]);

  if (!product) return <div className="p-16 text-center text-[#6B7B6C]" data-testid="product-loading">Loading…</div>;

  return (
    <div className="mx-auto max-w-7xl px-6 md:px-10 py-10" data-testid="product-detail-page">
      <Link to="/shop" className="inline-flex items-center gap-1 text-sm text-[#6B7B6C] hover:text-[#2C3E2D] mb-6" data-testid="back-to-shop">
        <ArrowLeft className="h-4 w-4" /> Back to shop
      </Link>
      <div className="grid grid-cols-1 md:grid-cols-2 gap-10">
        <div className="rounded-3xl overflow-hidden border border-[#E5E5E0] bg-white">
          <img src={product.image_url} alt={product.name} className="w-full h-full max-h-[70vh] object-cover" />
        </div>
        <div className="flex flex-col">
          <div className="tick-label mb-3">{product.category}</div>
          <h1 className="font-heading text-4xl sm:text-5xl text-[#2C3E2D] tracking-tight font-medium" data-testid="product-name">{product.name}</h1>
          <div className="mt-3 flex items-center gap-3">
            <div className="inline-flex items-center gap-1 text-sm text-[#2C3E2D]">
              <Star className="h-4 w-4 fill-[#F2CC8F] text-[#F2CC8F]" /> {product.rating?.toFixed(1)} · {Math.floor(Math.random() * 200 + 50)} reviews
            </div>
            <span className="text-xs text-[#6B7B6C]">Unit: {product.unit}</span>
          </div>
          <p className="mt-6 text-[#2C3E2D] leading-relaxed max-w-lg" data-testid="product-description">{product.description}</p>

          <div className="mt-8 flex items-baseline gap-3">
            <div className="font-heading text-4xl text-[#2C3E2D]" data-testid="product-detail-price">₹{product.price.toFixed(0)}</div>
            <span className="text-sm text-[#6B7B6C]">/ {product.unit}</span>
          </div>

          <div className="mt-6 flex items-center gap-4">
            <div className="inline-flex items-center rounded-full border border-[#E5E5E0] bg-white">
              <button onClick={() => setQty(Math.max(1, qty - 1))} className="p-2 hover:bg-[#F0EFEA] rounded-l-full" data-testid="pd-qty-dec">
                <Minus className="h-4 w-4" />
              </button>
              <span className="px-4 font-medium" data-testid="pd-qty-value">{qty}</span>
              <button onClick={() => setQty(qty + 1)} className="p-2 hover:bg-[#F0EFEA] rounded-r-full" data-testid="pd-qty-inc">
                <Plus className="h-4 w-4" />
              </button>
            </div>
            <Button
              onClick={() => addItem(product, qty)}
              className="btn-primary rounded-full h-12 px-6 inline-flex items-center gap-2"
              data-testid="pd-add-to-cart"
            >
              <ShoppingBag className="h-4 w-4" /> Add to basket · ₹{(product.price * qty).toFixed(0)}
            </Button>
          </div>

          <div className="mt-10 grid grid-cols-2 gap-3">
            <Info Icon={Truck} title="Free over ₹499" desc="Same-day city delivery" />
            <Info Icon={Leaf} title="Farm-fresh" desc="Sourced this morning" />
          </div>
        </div>
      </div>
    </div>
  );
};

const Info = ({ Icon, title, desc }) => (
  <div className="rounded-2xl border border-[#E5E5E0] bg-white p-4">
    <Icon className="h-4 w-4 text-[#81B29A] mb-2" />
    <div className="text-sm font-medium text-[#2C3E2D]">{title}</div>
    <div className="text-xs text-[#6B7B6C]">{desc}</div>
  </div>
);

export default ProductDetail;
